import React from 'react'
import moment from 'moment-timezone'
import classnames from 'classnames/bind'
// @ts-ignore no types
import { Text, Popup, Icon } from '@adc/ui-adc'
import * as blueprint from '@adc/blueprint'
import type {
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintLiveReactiveArgs,
} from '@adc/blueprint'
import configment from '@adc/configment'
import * as graph from '@adc/sdk'
import { GenericDate } from '@adc/sdk/types'
import { CombinedListingType, useListingContext } from 'common/context/listing'
import { date } from '../../../services/tps_formatter'
import getPrepareForAuctionContent from '../../../utils/prepareForAuctionContent'
import styles from '../index.scss'
import {
  formattedVenueAddress,
  isPostPoned,
  isReAuctionPending,
} from '../utils'
import appleCalendarLogo from '../../../images/logo_apple.svg'
import googleCalendarLogo from '../../../images/logo_google.svg'
import outlookLogo from '../../../images/logo_outlook.svg'

const cx = classnames.bind(styles)

const EVENT_DURATION = 30

declare global {
  interface Navigator {
    msSaveOrOpenBlob?: (blobOrBase64: Blob | string, filename: string) => void
    msSaveBlob?: (blob: any, defaultName?: string) => boolean
  }
}

const fileDownload = (filename: string, text: string) => {
  if (window.navigator.msSaveOrOpenBlob) {
    const blob = new Blob([text], { type: 'text/calendar' })
    window.navigator.msSaveBlob?.(blob, filename)
  } else {
    const element = document.createElement('a')
    element.setAttribute(
      'href',
      'data:text/calendar;charset=utf-8,' + encodeURIComponent(text)
    )
    element.setAttribute('download', filename)

    element.style.display = 'none'
    document.body.appendChild(element)

    element.click()

    document.body.removeChild(element)
  }
}

const ICSTime = (date: Date) => moment(date).format('YYYYMMDD[T]HHmmss')
const googleMapsTime = (date: Date) => moment(date).format('YYYYMMDD[T]HHmmss')

interface FormatICSParams {
  startTime: string
  endTime: string
  summary: string
  description: string
  location: string
}

export const formatICS = ({
  startTime,
  endTime,
  summary,
  description,
  location,
}: FormatICSParams) =>
  `BEGIN:VCALENDAR
VERSION:2.0
BEGIN:VEVENT
DTSTART:${startTime}
DTEND:${endTime}
SUMMARY:${summary}
DESCRIPTION:${description}
LOCATION:${location}
BEGIN:VALARM
TRIGGER:-PT30M
ACTION:DISPLAY
DESCRIPTION: Auction.com Auction
END:VALARM
END:VEVENT
END:VCALENDAR`

export const downloadCalendarEvent =
  (
    listing: CombinedListingType,
    segment:
      | AuctionBlueprintOnlineReactiveArgs['segment']
      | AuctionBlueprintLiveReactiveArgs['segment']
  ) =>
  () =>
    fileDownload('event.ics', formatICS(createCalendarEvent(listing, segment)))

const formatGoogleCalendarUrl = ({
  startTime,
  endTime,
  summary,
  description,
  location,
}: FormatICSParams) => `https://calendar.google.com/calendar/render?action=TEMPLATE
&dates=${startTime}/${endTime}
&location=${encodeURIComponent(location)}
&details=${encodeURIComponent(description)}
&text=${encodeURIComponent(summary)}`

export const createGoogleCalendarEvent =
  (
    listing: CombinedListingType,
    segment:
      | AuctionBlueprintOnlineReactiveArgs['segment']
      | AuctionBlueprintLiveReactiveArgs['segment']
  ) =>
  () =>
    window.open(
      formatGoogleCalendarUrl(createCalendarEvent(listing, segment, false)),
      '_blank'
    )

// TODO: this function really is a mess, a refactor for it could probably be much better if we split it by segment.__typename used fields (if possible)
// that just contain date and time together rather than having to get them from multiple places and patch them together
export const createCalendarEvent = (
  listing: CombinedListingType,
  segment:
    | AuctionBlueprintOnlineReactiveArgs['segment']
    | AuctionBlueprintLiveReactiveArgs['segment'],
  ics = true
) => {
  const hasNewAuctionDate = isPostPoned(listing) || isReAuctionPending(listing)
  const isOnlineBidding = Boolean(listing.online_auction_segment)
  const isPostPonedVal = isPostPoned(listing)

  let auctionDate
  let auctionTime

  let auctionDateFromSegment: GenericDate | string =
    segment?.__typename === 'OnlineAuctionSegment'
      ? segment?.start_date
      : (listing.trustee?.sale_time as string)

  if (isPostPonedVal)
    auctionDate = listing.listing_configuration?.postponement_new_auction_date
  else if (isReAuctionPending(listing))
    auctionDate = listing.auction?.provisional_sale_date
  else auctionDate = auctionDateFromSegment

  if (isPostPonedVal)
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    auctionTime = isOnlineBidding
      ? listing.listing_configuration?.postponement_new_auction_time // was bStatus.postponedTimeWithTimezone
      : date.format(
          listing.listing_configuration?.postponement_new_auction_time,
          'LT',
          'LTS'
        )
  else if (isReAuctionPending(listing))
    auctionTime = moment(auctionDateFromSegment).format('HH:MM:SSZ')
  else auctionTime = auctionDateFromSegment

  const startDate = moment(
    `${auctionDate} ${auctionTime}`,
    'YYYY-MM-DD hh:mm a Z'
  )

  if (segment?.__typename === 'LiveAuctionSegment') {
    auctionDateFromSegment = moment(
      `${listing.auction?.auction_date as string}T${
        auctionDateFromSegment as string
      }`
    ).toDate()
  }

  const adjustedStartDate: Date | undefined = auctionDateFromSegment
    ? moment(auctionDateFromSegment).toDate()
    : undefined

  // it will be undefined for live segments
  const adjustedEndDate: Date | undefined =
    segment?.__typename === 'OnlineAuctionSegment' && segment?.initial_end_date
      ? moment(segment.initial_end_date).toDate()
      : undefined

  const format = ics ? ICSTime : googleMapsTime
  const startTime = format(
    (isPostPonedVal ? startDate.toDate() : adjustedStartDate) ||
      startDate.toDate()
  )

  const endTime = format(
    // @ts-ignore format works with moment type and should spit out a string that looks like "20250416T113700"
    adjustedEndDate || moment(startTime).add(EVENT_DURATION, 'minutes')
  )

  const calculatedTimezone = blueprint.utils.calculateTimeZone()
  const formattedStartDate = moment(startTime)
    .tz(calculatedTimezone)
    .format(`MMMM D [at] h:mm a${hasNewAuctionDate ? ' z' : ''}`)
  const formattedEndDate = moment(endTime) // was bStatus.endDateTime
    .tz(calculatedTimezone)
    .format('MMMM D [at] h:mm a z')
  const [localAddress, federalAddress] = listing?.formatted_address || ['', '']
  const assetLocation = `${localAddress || 'TBD'}, ${federalAddress || 'TBD'}`
  const venueName = listing.venue?.venue_name // was bStatus.venueName
  const venueAddress = formattedVenueAddress(listing) //  bStatus.venueAddress

  const summary = `Reminder: ${
    listing.listing_configuration?.asset_type !==
    graph.enums.AssetType.Foreclosure
      ? 'Bank Owned'
      : 'Foreclosure'
  } ${
    !listing.auction?.is_online ? 'In Person Auction' : 'Online Auction' // was bStatus.isLiveEvent
  } for ${assetLocation}`

  const { note, list } = getPrepareForAuctionContent(listing, true)

  const pdpUrl = `${configment.get(
    'AUCTIONCOM_HOST',
    'https://www.auction.com'
  )}/${listing?.listing_page_path}`

  // The text looks weird because ICS only allows utf8 plaintext. This is the only way I could get cross-platform bold text
  // You can use https://yaytext.com/bold-italic/ (bold sans) to get bold text
  const descriptionText = isOnlineBidding
    ? `1. Hurry before it's too late! Register and place your bid online now:
${pdpUrl}

${
  hasNewAuctionDate
    ? `2. New Auction Start Date:
${formattedStartDate}`
    : `2. Duration:
${formattedStartDate} to ${formattedEndDate}`
}`
    : `1. Get updates on property status:
${pdpUrl}

2. Location:
${venueName}, ${venueAddress}

3. What to bring to the Auction:
Note: ${note}`

  list.unshift('\n') // Need extra blank line to get the first bullet point formatted
  const bulletPointsStr = list.join('\n• ')
  const description = ics
    ? (descriptionText + bulletPointsStr).split('\n').join('\\n') // ICS needs explicit newlines
    : descriptionText + bulletPointsStr

  return {
    startTime,
    endTime,
    summary,
    description,
    location: !blueprint.listing.utils.isTrustee(listing)
      ? 'Online Auction - See details to place a bid'
      : `${venueName}, ${venueAddress}`,
  }
}

const elmId = 'add-to-calendar'

interface CalendarPopup {
  isInTour?: boolean
}

const CalendarPopup = ({ isInTour }: CalendarPopup) => {
  const { listing, segment } = useListingContext()
  return (
    <div className={cx('u-mt-1')}>
      <Popup
        wide
        position='bottom center'
        className={cx('calendar-popup', { tour: isInTour })}
        trigger={
          <a data-elm-id={`${elmId}_trigger`}>
            <Icon
              name='calendar'
              size='small'
              color='info'
              iconStyle='light'
              className={cx('calendar-icon')}
            />
            Add to calendar
          </a>
        }
        on='click'
      >
        <div
          className={cx('calendar-cta')}
          onClick={createGoogleCalendarEvent(listing, segment)}
          data-elm-id={`${elmId}_gcal`}
        >
          <Text className={cx('u-display-flex')}>
            <img src={googleCalendarLogo} className={cx('u-mr-2')} />
            Google
          </Text>
        </div>
        <div
          className={cx('calendar-cta')}
          onClick={downloadCalendarEvent(listing, segment)}
          data-elm-id={`${elmId}_outlook`}
        >
          <Text>
            <img src={outlookLogo} className={cx('u-mr-2')} />
            Outlook
          </Text>
        </div>
        <div
          className={cx('calendar-cta')}
          onClick={downloadCalendarEvent(listing, segment)}
          data-elm-id={`${elmId}_ical`}
        >
          <Text>
            <img src={appleCalendarLogo} className={cx('u-mr-2')} />
            Calendar
          </Text>
        </div>
      </Popup>
    </div>
  )
}

export default CalendarPopup
